import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import bgEn from '../assets/images/friend-invite-bg-en.jpg';
import bgFr from '../assets/images/friend-invite-bg-fr.jpg';
import bgRw from '../assets/images/friend-invite-bg-rw.jpg';
import bgSw from '../assets/images/friend-invite-bg-sw.jpg';
import T, { LANGUAGES } from '../data/inviteTranslations';

// Draws the personal invite directly onto a <canvas> over the pre-designed
// 2160x2160 card artwork — the same "no DOM screenshot library" approach
// used for the dashboard's invitation feature, for the same reason: it's
// the only way to guarantee the exported image always has the right fonts
// and layout, independent of how the on-screen preview happens to render.
const CARD_SIZE = 2160;
const LANG_STORAGE_KEY = 'nu-card-lang';
const BG_SOURCES = { en: bgEn, fr: bgFr, rw: bgRw, sw: bgSw };
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Best-effort usage logging for the dashboard's invite count — fires once a
// download or share actually succeeds, and never blocks or breaks that
// action. If the backend is slow or unreachable, this silently does
// nothing: the visitor's invite still downloads/shares normally, we just
// miss that one count.
const logFriendInvite = (friendName, inviterName, language) => {
  try {
    fetch(`${BASE_URL}/api/friend-invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ friendName, inviterName, lang: language }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* never block the user's download/share */ }
};

const FONT_FACES = [
  'italic 600 68px "Lora"',
  '400 26px "Poppins"',
  '500 26px "Poppins"',
];

const clean = (s) => (s || '').replace(/\s+/g, ' ').trim().slice(0, 40);
const titleCase = (s) => s.replace(/(^|\s)(\S)/g, (m, a, b) => a + b.toUpperCase());

function drawRow(ctx, y, lead, name, size, blankW) {
  const leadFont = '400 17px "Poppins", sans-serif';
  ctx.font = leadFont;
  const lw = ctx.measureText(lead).width;
  const gap = 8;

  if (name) {
    let z = size;
    let nf;
    let nw;
    do {
      nf = `italic 600 ${z}px "Lora", Georgia, serif`;
      ctx.font = nf;
      nw = ctx.measureText(name).width;
      z -= 2;
    } while (lw + gap + nw > 860 && z > 24);
    const x = 540 - (lw + gap + nw) / 2;
    ctx.font = leadFont;
    ctx.fillStyle = '#181414';
    ctx.fillText(lead, x, y);
    ctx.font = nf;
    ctx.fillStyle = '#7a1630';
    ctx.fillText(name, x + lw + gap, y);
  } else {
    const x0 = 540 - (lw + gap + blankW) / 2;
    const x2 = x0 + lw + gap;
    ctx.font = leadFont;
    ctx.fillStyle = '#181414';
    ctx.fillText(lead, x0, y);
    ctx.strokeStyle = '#968787';
    ctx.lineWidth = 2;
    for (let xx = x2; xx < x2 + blankW; xx += 7) {
      ctx.beginPath();
      ctx.moveTo(xx, y + 7);
      ctx.lineTo(xx + 3, y + 7);
      ctx.stroke();
    }
  }
}

const fileName = (friendName, lang) => {
  const n = clean(friendName).replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '');
  return `None-Urabikoze-Invite-${lang.toUpperCase()}${n ? '-' + n : ''}.png`;
};

const detectInitialLang = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    const fromQuery = q.get('lang');
    if (fromQuery && T[fromQuery]) return fromQuery;
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && T[saved]) return saved;
    const nav = (navigator.language || '').slice(0, 2).toLowerCase();
    if (T[nav]) return nav;
  } catch (e) { /* ignore */ }
  return 'en';
};

const InviteFriend = () => {
  const [lang, setLang] = useState('en');
  const [friend, setFriend] = useState('');
  const [me, setMe] = useState('');
  const [status, setStatus] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [canShareFiles, setCanShareFiles] = useState(false);

  const canvasRef = useRef(null);
  const imgsRef = useRef({});
  const fontsReadyRef = useRef(false);
  const lastBlobRef = useRef(null);
  const objectUrlRef = useRef(null);
  const timerRef = useRef(null);

  const loadLangImage = (l) =>
    new Promise((resolve) => {
      const cached = imgsRef.current[l];
      if (cached && cached.complete) {
        resolve(cached);
        return;
      }
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = BG_SOURCES[l];
      imgsRef.current[l] = img;
    });

  function draw() {
    if (!fontsReadyRef.current) return;
    const img = imgsRef.current[lang];
    if (!img || !img.complete) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const friendName = titleCase(clean(friend));
    const myName = titleCase(clean(me));
    const L = T[lang];

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, CARD_SIZE, CARD_SIZE);
    ctx.drawImage(img, 0, 0, CARD_SIZE, CARD_SIZE);
    // Text is drawn at 1080-equivalent logical coordinates against the
    // 2160px (2x) artwork, matching how the reference design was authored.
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    drawRow(ctx, 458, L.dear, friendName ? friendName + ',' : '', 24, 220);
    drawRow(ctx, 780, L.love, myName, 21, 200);

    canvas.toBlob((blob) => {
      lastBlobRef.current = blob;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setPreviewUrl(url);
    }, 'image/png');
  }

  // `draw` closes over this render's lang/friend/me, so it goes stale the
  // moment any of those change. Anything that calls it asynchronously later
  // (font-ready callbacks, debounced timeouts) must go through this ref —
  // otherwise a late callback from an *earlier* render can redraw the canvas
  // with outdated state (e.g. clobbering a freshly-switched language back to
  // the previous one once its own stale timer finally fires).
  const drawRef = useRef(draw);
  drawRef.current = draw;

  // Load fonts once, detect the starting language, and prefill names from a
  // shareable link, e.g. /invite?to=Aline&from=Bernard&lang=rw
  useEffect(() => {
    let cancelled = false;
    const fontLoads = document.fonts
      ? Promise.all(FONT_FACES.map((f) => document.fonts.load(f))).catch(() => {})
      : Promise.resolve();
    fontLoads
      .then(() => (document.fonts && document.fonts.ready) || undefined)
      .then(() => {
        if (cancelled) return;
        fontsReadyRef.current = true;
        drawRef.current();
      });

    try {
      const probe = new File([new Blob(['x'], { type: 'image/png' })], 't.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [probe] })) setCanShareFiles(true);
    } catch (e) { /* share API not available */ }

    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get('to')) setFriend(q.get('to'));
      if (q.get('from')) setMe(q.get('from'));
    } catch (e) { /* ignore */ }

    setLang(detectInitialLang());

    return () => {
      cancelled = true;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load (or reuse) the chosen language's artwork, then draw.
  useEffect(() => {
    let cancelled = false;
    loadLangImage(lang).then(() => {
      if (!cancelled) drawRef.current();
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  // Debounced redraw as the admin types.
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => drawRef.current(), 120);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friend, me]);

  const handleLangChange = (code) => {
    setLang(code);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch (e) { /* ignore */ }
  };

  const handleDownload = () => {
    if (!lastBlobRef.current || !objectUrlRef.current) return;
    const name = fileName(friend, lang);
    const a = document.createElement('a');
    a.href = objectUrlRef.current;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setStatus(`${T[lang].ui.saved} ${name}.`);
    logFriendInvite(friend.trim(), me.trim(), lang);
  };

  // WhatsApp has no public API to pre-attach an image to a chat — a wa.me
  // link only accepts text, never a file. The device's native share sheet is
  // the only way to hand WhatsApp the actual image, so this button is only
  // shown where that's supported (see canShareFiles below); there's no
  // degraded "text only" fallback. Only the image is shared — no caption —
  // so the card speaks for itself instead of arriving with placeholder copy.
  const handleShareToWhatsApp = async () => {
    if (!lastBlobRef.current) return;
    try {
      const file = new File([lastBlobRef.current], fileName(friend, lang), { type: 'image/png' });
      await navigator.share({ files: [file] });
      setStatus(T[lang].ui.shared);
      logFriendInvite(friend.trim(), me.trim(), lang);
    } catch (e) {
      if (e && e.name !== 'AbortError') setStatus(T[lang].ui.shareFail);
    }
  };

  const canGenerate = clean(friend).length > 0 && clean(me).length > 0;
  const L = T[lang];

  return (
    <div className="frinv-page">
      <style>{`
        .frinv-page{
          --paper:#f5f2ed; --sheet:#fffdfa; --ink:#15120f; --muted:#837874; --line:#e8e0da;
          --accent-tint:#f0eeec;
          background:var(--paper);
          color:var(--ink); font-family:"Poppins",system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          font-size:15px; line-height:1.55; min-height:100vh;
        }
        .frinv-page *{ box-sizing:border-box; }
        .frinv-wrap{ max-width:980px; margin:0 auto; padding-inline:16px; padding-block:128px 64px; }

        .frinv-header{ display:flex; flex-direction:column; align-items:center; text-align:center; gap:10px; margin-bottom:40px; }
        .frinv-mark{ width:34px; height:34px; border-radius:50%; border:1.3px solid var(--ink); display:flex;
          align-items:center; justify-content:center; color:var(--ink); margin-bottom:4px; }
        .frinv-mark svg{ width:14px; height:14px; }
        .frinv-eyebrow{ font-size:11.5px; letter-spacing:.18em; text-transform:uppercase; color:var(--ink); font-weight:600; }
        .frinv-h1{ font-family:"Lora",Georgia,"Times New Roman",serif; font-style:italic; font-weight:600;
          font-size:clamp(30px,4.6vw,44px); line-height:1.1; margin:0; }
        .frinv-lede{ margin:0; color:var(--muted); max-width:50ch; font-size:14.5px; }

        .frinv-panel{
          background:var(--sheet); border:1px solid var(--line); border-radius:22px;
          box-shadow:0 1px 2px rgba(40,15,20,.03), 0 24px 60px -24px rgba(40,15,20,.18);
          overflow:hidden;
        }
        .frinv-grid{ display:grid; grid-template-columns:minmax(0,1fr) minmax(0,380px); }
        @media (max-width:860px){ .frinv-grid{ grid-template-columns:minmax(0,1fr); } }

        .frinv-form{ display:flex; flex-direction:column; gap:20px; min-width:0; padding:40px; }
        @media (max-width:560px){ .frinv-form{ padding:28px 22px; } }
        .frinv-field{ display:flex; flex-direction:column; gap:7px; }
        .frinv-field label{ font-size:12.5px; font-weight:600; letter-spacing:.03em; color:var(--ink); }
        .frinv-input-wrap{ position:relative; display:flex; align-items:center; }
        .frinv-input-wrap svg{ position:absolute; left:14px; width:16px; height:16px; color:var(--muted); pointer-events:none; }
        .frinv-field input[type=text]{
          font:inherit; font-size:15.5px; padding:13px 14px 13px 40px; border:1.3px solid var(--line);
          border-radius:12px; background:#fff; color:var(--ink); width:100%; transition:border-color .15s, box-shadow .15s;
        }
        .frinv-field input[type=text]::placeholder{ color:#b7aeae; }
        .frinv-field input[type=text]:focus{
          outline:none; border-color:var(--ink); box-shadow:0 0 0 4px var(--accent-tint);
        }
        .frinv-hint{ font-size:12px; color:var(--muted); }
        .frinv-hint--warn{ color:var(--ink); font-weight:500; margin-top:-8px; }

        .frinv-langs{ display:flex; flex-wrap:wrap; gap:8px; }
        .frinv-lang-btn{
          font:inherit; font-size:13px; font-weight:600; padding:9px 16px; border-radius:999px;
          border:1.3px solid var(--line); background:#fff; color:var(--ink); cursor:pointer;
          transition:background .15s, border-color .15s, color .15s;
        }
        .frinv-lang-btn:hover{ border-color:var(--ink); }
        .frinv-lang-btn.active{ background:var(--ink); border-color:var(--ink); color:#fff; }

        .frinv-actions{ display:flex; flex-wrap:wrap; gap:10px; margin-top:4px; }
        .frinv-btn{
          font:inherit; font-size:14.5px; font-weight:600; border-radius:999px; padding:13px 22px; cursor:pointer;
          border:1.5px solid var(--ink); text-decoration:none; display:inline-flex; align-items:center; gap:9px;
          line-height:1; transition:background .15s, color .15s, border-color .15s, transform .15s, box-shadow .15s;
        }
        .frinv-btn:active{ transform:translateY(1px); }
        .frinv-btn svg{ width:15px; height:15px; flex-shrink:0; }
        .frinv-btn--primary{ background:var(--ink); color:#fff; box-shadow:0 10px 24px -10px rgba(21,18,15,.45); }
        .frinv-btn--primary:hover{ background:#000; border-color:#000; }
        .frinv-btn--whatsapp{ background:#25D366; color:#fff; border-color:#25D366; }
        .frinv-btn--whatsapp:hover{ background:#1ebe5a; border-color:#1ebe5a; }
        .frinv-btn--whatsapp svg{ width:17px; height:17px; }
        .frinv-btn:disabled{
          opacity:.4; cursor:not-allowed; box-shadow:none; transform:none; pointer-events:none;
        }

        .frinv-status{
          display:flex; align-items:center; gap:7px; font-size:13px; color:var(--ink); margin:0;
          min-height:1.4em; opacity:0; transition:opacity .2s;
        }
        .frinv-status.show{ opacity:1; }
        .frinv-status svg{ width:14px; height:14px; flex-shrink:0; }

        .frinv-facts{ display:flex; border-top:1px solid var(--line); background:var(--ink); }
        .frinv-fact{ flex:1; padding:16px 18px; border-right:1px solid rgba(255,255,255,.14); min-width:0; }
        .frinv-fact:last-child{ border-right:0; }
        .frinv-fact dt{ font-size:10px; letter-spacing:.14em; text-transform:uppercase; color:rgba(255,255,255,.55); font-weight:700; margin:0 0 4px; }
        .frinv-fact dd{ margin:0; font-size:12.5px; font-weight:600; color:#fff; line-height:1.3; }
        .frinv-fact-link{ color:#fff; text-decoration:underline; text-underline-offset:2px; }
        @media (max-width:560px){
          .frinv-facts{ flex-direction:column; }
          .frinv-fact{ border-right:0; border-top:1px solid rgba(255,255,255,.14); }
          .frinv-fact:first-child{ border-top:0; }
          .frinv-fact{ display:flex; align-items:center; justify-content:space-between; gap:12px; }
          .frinv-fact dt{ margin:0; }
        }

        .frinv-preview{
          position:relative; background:#efe9e2; aspect-ratio:1/1; align-self:center;
        }
        .frinv-preview::before{
          content:""; position:absolute; inset:0; z-index:0;
          background:linear-gradient(135deg, rgba(21,18,15,.06), transparent 55%);
        }
        .frinv-preview img{
          display:block; width:100%; height:100%; object-fit:cover; position:relative; z-index:1;
          opacity:0; transition:opacity .25s ease;
        }
        .frinv-preview img.ready{ opacity:1; }
        @media (max-width:860px){ .frinv-preview{ order:-1; } }

        @media (prefers-reduced-motion:no-preference){
          .frinv-header, .frinv-panel{ animation:frinv-rise .7s cubic-bezier(.2,.7,.2,1) both; }
          .frinv-panel{ animation-delay:.1s; }
          @keyframes frinv-rise{ from{ opacity:0; transform:translateY(14px); } }
        }
      `}</style>

      <div className="frinv-wrap">
        <header className="frinv-header">
          <span className="frinv-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 21s-7.5-4.8-10-9.3C.3 7.8 2.4 4 6.3 4c2 0 3.6 1 4.7 2.6C12.1 5 13.7 4 15.7 4c3.9 0 6 3.8 4.3 7.7C20 16.2 12 21 12 21Z" /></svg>
          </span>
          <span className="frinv-eyebrow">{L.ui.eyebrow}</span>
          <h1 className="frinv-h1">{L.ui.h1}</h1>
          <p className="frinv-lede">{L.ui.lede}</p>
        </header>

        <div className="frinv-panel">
          <div className="frinv-grid">
            <form className="frinv-form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <div className="frinv-field">
                <label>{L.ui.langLabel}</label>
                <div className="frinv-langs" role="radiogroup" aria-label={L.ui.langLabel}>
                  {LANGUAGES.map(({ code, label }) => (
                    <button
                      key={code}
                      type="button"
                      role="radio"
                      aria-checked={lang === code}
                      className={`frinv-lang-btn${lang === code ? ' active' : ''}`}
                      onClick={() => handleLangChange(code)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="frinv-field">
                <label htmlFor="frinv-friend">{L.ui.friendLabel}</label>
                <div className="frinv-input-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM4 21c1-4 4.5-6 8-6s7 2 8 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <input
                    id="frinv-friend"
                    type="text"
                    maxLength={40}
                    value={friend}
                    onChange={(e) => setFriend(e.target.value)}
                  />
                </div>
                <span className="frinv-hint">{L.ui.friendHint}</span>
              </div>
              <div className="frinv-field">
                <label htmlFor="frinv-me">{L.ui.meLabel}</label>
                <div className="frinv-input-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM4 21c1-4 4.5-6 8-6s7 2 8 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  <input
                    id="frinv-me"
                    type="text"
                    maxLength={40}
                    value={me}
                    onChange={(e) => setMe(e.target.value)}
                  />
                </div>
                <span className="frinv-hint">{L.ui.meHint}</span>
              </div>

              <div className="frinv-actions">
                <button
                  type="button"
                  className="frinv-btn frinv-btn--primary"
                  onClick={handleDownload}
                  disabled={!canGenerate}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  {L.ui.download}
                </button>
                {canShareFiles && (
                  <button
                    type="button"
                    className="frinv-btn frinv-btn--whatsapp"
                    onClick={handleShareToWhatsApp}
                    disabled={!canGenerate}
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                    {L.ui.wa}
                  </button>
                )}
              </div>
              {!canGenerate && (
                <p className="frinv-hint frinv-hint--warn">{L.ui.enterBoth}</p>
              )}
              <p className={`frinv-status${status ? ' show' : ''}`} role="status">
                {status && (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    {status}
                  </>
                )}
              </p>
            </form>

            <div className="frinv-preview">
              <img
                className={previewUrl ? 'ready' : ''}
                src={previewUrl || undefined}
                alt={L.ui.alt}
                width="2160"
                height="2160"
              />
            </div>
          </div>

          <dl className="frinv-facts">
            {L.ui.facts.map(([label, value], i) => {
              const isRegister = i === 3 && value.includes('elayonemusic.org/register');
              const parts = isRegister ? value.split('elayonemusic.org/register') : null;
              return (
                <div className="frinv-fact" key={label}>
                  <dt>{label}</dt>
                  <dd>
                    {isRegister ? (
                      <>
                        {parts[0]}
                        <a href="/register" className="frinv-fact-link">elayonemusic.org/register</a>
                        {parts[1]}
                      </>
                    ) : value}
                  </dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>

      <canvas ref={canvasRef} width={CARD_SIZE} height={CARD_SIZE} hidden />
    </div>
  );
};

export default InviteFriend;
